// Browser-Spiegel von services/mail_pruefer.py (ADR 0368). Der Server ist maßgeblich und sperrt;
// dieser Spiegel zeigt dieselben Verstöße schon beim Tippen. Beide laufen gegen dieselben Fälle in
// tests/fixtures/mail_pruefer_faelle.json. Wer eine Regel ändert, ändert sie an beiden Orten.
import { vorlageById, PLATZ_TITEL, type Platz } from './postCallVorlagen';

export interface Verstoss { regel: string; grund: string }

export const SAISON_VORLAGEN = new Set(['F3', 'E2', 'H1']);
export const Q4_FRIST_STANDARD: [number, number] = [11, 15];

const LUECKE_RE = /\[[^\]\n]+\]/;

const SPERRWOERTER: [string, string][] = [
  ['unverbindlich', '„unverbindlich“ klingt nach Verkauf'],
  ['ganz flexibel', '„ganz flexibel“ ist kein Vorschlag'],
  ['kurz zu uns', '„Kurz zu uns“ wiederholt den Pitch'],
  ['kurz zu meiner letzten', '„kurz zu meiner letzten Mail“ bringt nichts Neues'],
  ['wollte nur nachhaken', '„nur nachhaken“ bringt nichts Neues'],
  ['grossteil', '„Grossteil“ ist eine zu grosse Behauptung'],
  ['großteil', '„Großteil“ ist eine zu grosse Behauptung'],
  ['nur noch heute', 'künstlicher Druck'],
  ['refund', '„Refund“: Gutschrift sagen'],
];

/** Gilt der Q4-Anlass noch? Oktober bis zur Frist (Monat, Tag). */
export function saisonOffen(heute: Date = new Date(), frist: [number, number] = Q4_FRIST_STANDARD): boolean {
  const m = heute.getMonth() + 1;
  return m === 10 || (m === frist[0] && heute.getDate() <= frist[1]);
}

export function pruefeHart(text: string, betreff: string, vorlage: string | null | undefined,
                           heute: Date = new Date(), frist: [number, number] = Q4_FRIST_STANDARD): Verstoss[] {
  const fehler: Verstoss[] = [];
  if (!text.trim()) return [{ regel: 'leer', grund: 'Der Text ist leer' }];
  const voll = `${betreff}\n${text}`;
  const klein = voll.toLowerCase();

  const luecke = LUECKE_RE.exec(voll);
  if (luecke) fehler.push({ regel: 'luecke', grund: `Offene Lücke: ${luecke[0]}` });
  for (const [wort, grund] of SPERRWOERTER) if (klein.includes(wort)) fehler.push({ regel: 'sperrwort', grund });
  if (/60\s*tage/.test(klein) && !/messung|beleg/.test(klein)) {
    fehler.push({ regel: 'sechzig_tage', grund: '„60 Tage“ ohne „Belege erst ab Messbeginn“' });
  }
  if (klein.includes('rückwirkend') && !/messung|beleg|einbau/.test(klein)) {
    fehler.push({ regel: 'rueckwirkend', grund: '„rückwirkend“ ohne „ab Messbeginn“ verspricht zu viel' });
  }
  if (vorlage && SAISON_VORLAGEN.has(vorlage) && !saisonOffen(heute, frist)) {
    fehler.push({ regel: 'saison', grund: 'Q4-Frist abgelaufen, diese Vorlage gilt nicht mehr' });
  }
  return fehler;
}

export interface MailPruefung { hart: Verstoss[]; hinweise: string[]; leerePflicht: Platz[] }

/** Harte Regeln des Servers, dazu die nur im Browser: leerer Pflicht-Platz sperrt, der Rest sind Hinweise. */
export function pruefeMail(opts: {
  betreff: string; text: string; vorlageId: string; platzTexte: Partial<Record<Platz, string>>;
  anrede: 'du' | 'sie'; land: string | null; heute?: Date; frist?: [number, number];
}): MailPruefung {
  const heute = opts.heute ?? new Date();
  const v = vorlageById(opts.vorlageId);
  const hart = pruefeHart(opts.text, opts.betreff, opts.vorlageId, heute, opts.frist);
  const leerePflicht = (v?.pflicht ?? []).filter((p) => !(opts.platzTexte[p] ?? '').trim());
  for (const p of leerePflicht) hart.push({ regel: 'pflicht_leer', grund: `${PLATZ_TITEL[p]} fehlt` });

  const hinweise: string[] = [];
  if (v && opts.text.trim()) {
    const worte = opts.text.trim().split(/\s+/).length;
    if (worte < v.woerter[0] || worte > v.woerter[1]) {
      hinweise.push(`${worte} Wörter, die Vorlage sieht ${v.woerter[0]} bis ${v.woerter[1]} vor`);
    }
    const fragen = (opts.text.replace(/„[^“]*“/g, '').match(/\?/g) ?? []).length;
    const soll = v.fragen - (v.id === 'D1' ? 1 : 0);
    if (fragen > soll) hinweise.push(`${fragen} Fragen: mehr als eine Bitte?`);
  }
  const ohneZitat = opts.text.replace(/„[^“]*“/g, '');
  if (opts.anrede === 'sie' && /\b(du|dir|dich|euch|euer|eure)\b/i.test(ohneZitat)) hinweise.push('Lead ist per Sie, im Text steht Du');
  if (opts.anrede === 'du' && /\b(Ihnen|Ihr|Ihre|Ihren)\b/.test(ohneZitat)) hinweise.push('Lead ist per Du, im Text steht Sie');
  if (opts.land === 'ch' && /ß/.test(opts.text)) hinweise.push('Schweiz: ß im Text, ss nehmen');
  return { hart, hinweise, leerePflicht };
}
